import React, { useEffect, useRef, useState } from 'react';
import './RadialAudioPlayer.scss';

interface RadialAudioPlayerProps {
  audioSrc: string;
  playerId: string; // Add a unique ID for each player
  size?: number;
}

// Create a global state to track the currently playing audio
let currentlyPlayingId: string | null = null;
const audioPlayers: { [key: string]: HTMLAudioElement | null } = {};

const RadialAudioPlayer: React.FC<RadialAudioPlayerProps> = ({
  audioSrc,
  playerId,
  size = 100,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const progressCircleRef = useRef<HTMLDivElement>(null);
  const radius = size / 4;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Register this audio element
    audioPlayers[playerId] = audio;

    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleError = () => {
      setHasError(true);
      setIsLoading(false);
    };

    const updateProgress = () => {
      if (audio.duration) {
        const progress = (audio.currentTime / audio.duration) * 100;
        setProgress(progress);
      }
    };

    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', () => {
      setIsPlaying(false);
      currentlyPlayingId = null;
    });
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', () => {
        setIsPlaying(false);
        currentlyPlayingId = null;
      });
      audio.removeEventListener('error', handleError);

      // Clean up
      delete audioPlayers[playerId];
      if (currentlyPlayingId === playerId) {
        currentlyPlayingId = null;
      }
    };
  }, [playerId, audioSrc]);

  const togglePlayPause = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading || hasError || !audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      currentlyPlayingId = null;
    } else {
      // 1. ZAMKNIJ INNE (najlepiej przez event, nie blokując wątku)
      if (currentlyPlayingId && currentlyPlayingId !== playerId) {
        window.dispatchEvent(
          new CustomEvent('audioStopped', {
            detail: { playerId: currentlyPlayingId },
          }),
        );
      }

      // 2. ODTWÓRZ (iOS wymaga, by było to jak najbliżej onClick)
      try {
        await audioRef.current.play();
        setIsPlaying(true);
        currentlyPlayingId = playerId;
      } catch (error) {
        console.error('Błąd iOS Playback:', error);
        // Na iOS często pomaga wywołanie .load() przed .play() jeśli src się zmieniło
        audioRef.current.load();
        audioRef.current.play();
      }
    }
  };

  // Listen for stop events from other players
  useEffect(() => {
    const handleAudioStopped = (e: CustomEvent) => {
      if (e.detail.playerId === playerId && isPlaying) {
        setIsPlaying(false);
        setProgress(0);
      }
    };

    // Add event listener for custom event
    const eventListener = (e: Event) => {
      handleAudioStopped(e as CustomEvent);
    };

    window.addEventListener('audioStopped' as any, eventListener);

    return () => {
      window.removeEventListener('audioStopped' as any, eventListener);
    };
  }, [isPlaying, playerId]);

  const progressOffset = circumference - (progress / 100) * circumference;

  // Determine if button should be disabled
  const isButtonDisabled = isLoading || hasError || !audioSrc;

  useEffect(() => {
    if (audioSrc && audioRef.current) {
      audioRef.current.load(); // Przygotowuje dekoder na iOS
    }
  }, [audioSrc]);

  return (
    <>
      <audio key={audioSrc} ref={audioRef} src={audioSrc} preload="metadata" playsInline />

      <div
        className="progress-circle"
        ref={progressCircleRef}
        style={isButtonDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            className="progress-background"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth="8"
          />
          <circle
            className="progress-bar"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={progressOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>

        <div
          className={`control-button ${isButtonDisabled ? 'disabled' : ''}`}
          onClick={togglePlayPause}
          style={isButtonDisabled ? { pointerEvents: 'none', opacity: 0.7 } : {}}
        >
          {hasError ? (
            <ErrorIcon />
          ) : isLoading ? (
            <LoadingIcon />
          ) : isPlaying ? (
            <PauseIcon />
          ) : (
            <PlayIcon />
          )}
        </div>
      </div>
    </>
  );
};

// Icon components
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const LoadingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path
      d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"
      opacity="0.4"
    />
    <path d="M12 2a10 10 0 0 0-10 10h2a8 8 0 0 1 8-8z">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

const ErrorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="12" r="10" opacity="0.4" />
    <path
      fill="#fff"
      d="M15.707 8.293a1 1 0 0 0-1.414 0L12 10.586 9.707 8.293a1 1 0 0 0-1.414 1.414L10.586 12l-2.293 2.293a1 1 0 1 0 1.414 1.414L12 13.414l2.293 2.293a1 1 0 0 0 1.414-1.414L13.414 12l2.293-2.293a1 1 0 0 0 0-1.414z"
    />
  </svg>
);

export default RadialAudioPlayer;
